import * as XLSX from 'xlsx';
import type { LegacyUserMigrationExportPayload } from '../../services/legacyUserManager';

const str = (value: unknown): string => {
  if (value == null) return '';
  return String(value);
};

export type V1ContactExcelRow = Record<string, string | number | boolean>;
export type V1ConfigExcelRow = Record<string, string | number | boolean>;

const V1_UTENTI_COLUMNS = [
  'legacy_user_id',
  'email',
  'first_name',
  'last_name',
  'full_name',
  'title',
  'type',
  'role',
  'profession',
  'disciple',
  'contract',
  'fiscal_code',
  'region',
  'address',
  'website',
  'vat_number',
  'sdi_number',
  'created_at',
  'courses_count',
  'courses',
  'abbonamenti_count',
  'abbonamenti',
  'configurations_count',
  'devices_count',
  'patients_count',
  'answers_count',
  'feedback_count',
] as const;

const V1_CONFIG_COLUMNS = [
  'legacy_user_id',
  'email',
  'configuration_id',
  'patient_id',
  'food_list_id',
  'room',
  'status',
  'hr_min',
  'hr_max',
  'difficulty',
  'start',
  'is_recording',
  'is_buffet',
  'user_device_id',
  'deleted_at',
  'created_at',
  'updated_at',
] as const;

const formatDate = (value: unknown): string => {
  const s = str(value).trim();
  if (!s) return '';
  // Keep ISO date prefix when present (YYYY-MM-DD…)
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s;
};

const formatCourses = (
  entitlements: Array<Record<string, unknown>>
): string =>
  entitlements
    .map((row) => {
      const name = str(row.course_name) || `course ${str(row.course_id)}`;
      const parts = [name];
      const completed = str(row.completed_at);
      if (completed) parts.push(`completed=${completed}`);
      if (row.answered_the_questions === 1 || row.answered_the_questions === true) {
        parts.push('answered');
      }
      if (row.feedback_sent === 1 || row.feedback_sent === true) {
        parts.push('feedback');
      }
      const attempts = row.number_of_attempts;
      if (attempts != null && attempts !== '') {
        parts.push(`attempts=${str(attempts)}`);
      }
      return parts.join(' | ');
    })
    .filter(Boolean)
    .join('; ');

/**
 * Abbonamenti = video entitlements (experiences / products unlock) + Stripe subscriptions.
 * Always include inizio (created_at) and scadenza (valid_until / ends_at) when present.
 */
const formatAbbonamenti = (
  videos: Array<Record<string, unknown>>,
  subscriptions: Array<Record<string, unknown>>
): string => {
  const videoParts = videos.map((row) => {
    const name = str(row.video_name) || `video ${str(row.video_id)}`;
    const category = str(row.video_category);
    const subLabel = str(row.subscription);
    const label = [name, category || null, subLabel || null]
      .filter(Boolean)
      .join(' / ');
    const inizio = formatDate(row.created_at);
    const scadenza = formatDate(row.valid_until);
    const parts = [label];
    if (inizio) parts.push(`inizio=${inizio}`);
    if (scadenza) parts.push(`scadenza=${scadenza}`);
    return parts.join(' | ');
  });

  const stripeParts = subscriptions.map((row) => {
    const name = str(row.name) || `subscription ${str(row.id)}`;
    const status = str(row.stripe_status);
    const label = status ? `${name} (${status})` : name;
    const inizio = formatDate(row.created_at);
    const scadenza = formatDate(row.ends_at) || formatDate(row.trial_ends_at);
    const parts = [label];
    if (inizio) parts.push(`inizio=${inizio}`);
    if (scadenza) parts.push(`scadenza=${scadenza}`);
    return parts.join(' | ');
  });

  return [...videoParts, ...stripeParts].filter(Boolean).join('; ');
};

/** Flatten one v1 migration export into a user inventory row (courses + abbonamenti). */
export const v1ExportToContactRow = (
  payload: LegacyUserMigrationExportPayload
): V1ContactExcelRow => {
  const source = (payload.source ?? {}) as Record<string, unknown>;
  const profile = (source.profile ?? {}) as Record<string, unknown>;
  const entitlements = (
    Array.isArray(source.entitlements) ? source.entitlements : []
  ) as Array<Record<string, unknown>>;
  const videos = (
    Array.isArray(source.videos) ? source.videos : []
  ) as Array<Record<string, unknown>>;
  const subscriptions = (
    Array.isArray(source.subscriptions) ? source.subscriptions : []
  ) as Array<Record<string, unknown>>;
  const configurations = Array.isArray(source.configurations)
    ? source.configurations
    : [];
  const devices = Array.isArray(source.devices) ? source.devices : [];
  const patients = Array.isArray(source.patients) ? source.patients : [];
  const answers = Array.isArray(source.answers) ? source.answers : [];
  const feedback = Array.isArray(source.feedback) ? source.feedback : [];

  return {
    legacy_user_id: payload.legacy_user_id,
    email: payload.email ?? '',
    first_name: str(profile.first_name ?? source.first_name),
    last_name: str(profile.last_name ?? source.last_name),
    full_name: str(profile.full_name ?? source.full_name),
    title: str(profile.title),
    type: str(profile.type),
    role: str(profile.role),
    profession: str(profile.profession),
    disciple: str(profile.disciple),
    contract: str(profile.contract),
    fiscal_code: str(profile.fiscal_code),
    region: str(profile.region),
    address: str(profile.address),
    website: str(profile.website),
    vat_number: str(profile.vat_number),
    sdi_number: str(profile.sdi_number),
    created_at: str(profile.created_at ?? source.created_at),
    courses_count: entitlements.length,
    courses: formatCourses(entitlements),
    abbonamenti_count: videos.length + subscriptions.length,
    abbonamenti: formatAbbonamenti(videos, subscriptions),
    configurations_count: configurations.length,
    devices_count: devices.length,
    patients_count: patients.length,
    answers_count: answers.length,
    feedback_count: feedback.length,
  };
};

/** One row per vapor configuration for the configs sheet. */
export const v1ExportToConfigRows = (
  payload: LegacyUserMigrationExportPayload
): V1ConfigExcelRow[] => {
  const source = (payload.source ?? {}) as Record<string, unknown>;
  const configurations = (
    Array.isArray(source.configurations) ? source.configurations : []
  ) as Array<Record<string, unknown>>;

  return configurations.map((row) => ({
    legacy_user_id: payload.legacy_user_id,
    email: payload.email ?? '',
    configuration_id: Number(row.id) || 0,
    patient_id: Number(row.patient_id) || 0,
    food_list_id:
      row.food_list_id == null || row.food_list_id === ''
        ? ''
        : Number(row.food_list_id),
    room: str(row.room),
    status: str(row.status),
    hr_min: row.hr_min == null || row.hr_min === '' ? '' : Number(row.hr_min),
    hr_max: row.hr_max == null || row.hr_max === '' ? '' : Number(row.hr_max),
    difficulty:
      row.difficulty == null || row.difficulty === ''
        ? ''
        : Number(row.difficulty),
    start: row.start == null || row.start === '' ? '' : Number(row.start),
    is_recording:
      row.is_recording === true ||
      row.is_recording === 1 ||
      row.is_recording === '1'
        ? 'yes'
        : row.is_recording === false ||
            row.is_recording === 0 ||
            row.is_recording === '0'
          ? 'no'
          : str(row.is_recording),
    is_buffet:
      row.is_buffet === true || row.is_buffet === 1 || row.is_buffet === '1'
        ? 'yes'
        : row.is_buffet === false ||
            row.is_buffet === 0 ||
            row.is_buffet === '0'
          ? 'no'
          : str(row.is_buffet),
    user_device_id: Number(row.user_device_id) || 0,
    deleted_at: str(row.deleted_at),
    created_at: str(row.created_at),
    updated_at: str(row.updated_at),
  }));
};

/**
 * Two sheets:
 * - Utenti: profile + courses + abbonamenti (videos) + counts
 * - Configs: one row per user configuration
 */
export const buildV1ContactsExcelWorkbook = (
  payloads: LegacyUserMigrationExportPayload[]
): XLSX.WorkBook => {
  const userRows = payloads.map((payload) => v1ExportToContactRow(payload));
  const configRows = payloads.flatMap((payload) =>
    v1ExportToConfigRows(payload)
  );

  const workbook = XLSX.utils.book_new();
  const utentiSheet =
    userRows.length > 0
      ? XLSX.utils.json_to_sheet(userRows)
      : XLSX.utils.aoa_to_sheet([V1_UTENTI_COLUMNS.slice()]);
  XLSX.utils.book_append_sheet(workbook, utentiSheet, 'Utenti');

  const configsSheet =
    configRows.length > 0
      ? XLSX.utils.json_to_sheet(configRows)
      : XLSX.utils.aoa_to_sheet([V1_CONFIG_COLUMNS.slice()]);
  XLSX.utils.book_append_sheet(workbook, configsSheet, 'Configs');

  return workbook;
};

export const v1ContactsExcelFilename = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `legacy-v1-inventory-${y}${m}${day}.xlsx`;
};
